// Copyright (c) 2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { CurrencyUtils } from "../../../../io/hyperify/core/CurrencyUtils";
import { filter } from "../../../../io/hyperify/core/functions/filter";
import { find } from "../../../../io/hyperify/core/functions/find";
import { map } from "../../../../io/hyperify/core/functions/map";
import { moment } from "../../../../io/hyperify/core/modules/moment";
import { getPriceTypeTranslationToken } from "../../../../io/hyperify/core/store/constants/storeTranslation";
import {
    createProduct,
    Product,
} from "../../../../io/hyperify/core/store/types/product/Product";
import {
    createProductPrice,
    ProductPrice,
} from "../../../../io/hyperify/core/store/types/product/ProductPrice";
import { ProductPriceType } from "../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { ProductType } from "../../../../io/hyperify/core/store/types/product/ProductType";
import { TranslationFunction } from "../../../../io/hyperify/core/types/TranslationFunction";
import { TranslationParams } from "../../../../io/hyperify/core/types/TranslationParams";
import {
    T_DOMAIN_PRODUCT_SUMMARY,
    T_OUTLET_PRODUCT_SUMMARY,
    T_OUTLET_PRODUCT_TITLE,
} from "../constants/translation";
import {
    getSendanorProductIdForDomain,
    getSendanorProductIdForOutlet,
} from "../types/SendanorProductId";

export class SendanorProductUtils {

    /**
     *
     * @param id
     * @param eurPriceIncludingVat
     * @param vatPercent
     * @param published
     * @param stockAmount
     * @param stockSold
     * @param order
     */
    public static createOutletProduct (
        id : string,
        eurPriceIncludingVat : number,
        vatPercent : number,
        published : boolean = true,
        stockAmount : number = 1,
        stockSold : number = 0,
        order : number = 0,
    ) : Product {
        id = getSendanorProductIdForOutlet( id )
        return createProduct(
            id,
            ProductType.OUTLET,
            T_OUTLET_PRODUCT_TITLE(id),
            T_OUTLET_PRODUCT_SUMMARY(id),
            [],
            [
                createProductPrice(
                    eurPriceIncludingVat,
                    vatPercent,
                    ProductPriceType.ONCE,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    0,
                    0,
                    false,
                    undefined,
                    true,
                    undefined,
                )
            ],
            stockSold,
            stockAmount,
            true,
            false,
            published,
            undefined,
            undefined,
            undefined,
            order,
        );
    }

    /**
     *
     * @param domain
     * @param type
     * @param prices
     * @param published
     * @param stockSold
     * @param order
     */
    public static createDomainProduct (
        domain : string,
        type : ProductType,
        prices : readonly {
            eurPriceIncludingVat : number,
            vatPercent : number,
            priceType : ProductPriceType,
        }[],
        published : boolean = true,
        stockSold : number = 0,
        order : number = 0,
    ) : Product {
        return createProduct(
            getSendanorProductIdForDomain( domain ),
            type,
            domain,
            T_DOMAIN_PRODUCT_SUMMARY( type ),
            [],
            map(
                prices,
                (item) => {
                    return createProductPrice(
                        item.eurPriceIncludingVat,
                        item.vatPercent,
                        item.priceType,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        0,
                        0,
                        false,
                        undefined,
                        true,
                        undefined,
                    )
                }
            ),
            stockSold,
            1,
            true,
            false,
            published,
            undefined,
            undefined,
            undefined,
            order,
        );
    }

    /**
     *
     * @param productPrice
     * @param dateTimeFormat
     * @param t
     * @param extraParams
     */
    public static createTranslationParamsFromPrice (
        productPrice   : ProductPrice | undefined,
        dateTimeFormat : string,
        t              : TranslationFunction,
        extraParams   ?: TranslationParams,
    ) : TranslationParams {

        const discountFrom    : string | undefined = productPrice?.discountFrom ?? (new Date()).toISOString();
        const discountTo      : string | undefined = productPrice?.discountTo;
        const discountActive  : boolean = discountTo ? moment().isBetween(moment(discountFrom), moment(discountTo)) : moment().isAfter(moment(discountFrom));
        const discountPercent : number | undefined = discountActive ? productPrice?.discountPercent : undefined;

        return {
            ...(extraParams ? extraParams : {}),
            AMOUNT : productPrice ? CurrencyUtils.stringifyToShortSum( CurrencyUtils.getSumWithDiscount( productPrice.sum, discountPercent ) ) : '',
            VATLESS_AMOUNT : productPrice ? CurrencyUtils.stringifyToShortSum( CurrencyUtils.getVatlessSum( CurrencyUtils.getSumWithDiscount( productPrice.sum, discountPercent ), productPrice.vatPercent ) ) : '',
            PERIOD : productPrice ? t( getPriceTypeTranslationToken( productPrice.type ) ) : '',
            ORIG_AMOUNT : productPrice ? CurrencyUtils.stringifyToShortSum( productPrice.sum ) : '',
            ORIG_VATLESS_AMOUNT : productPrice ? CurrencyUtils.stringifyToShortSum( CurrencyUtils.getVatlessSum( productPrice.sum, productPrice.vatPercent ) ) : '',
            DISCOUNT_TO : discountPercent ? moment( discountTo ).format( dateTimeFormat ) : '',
            DISCOUNT : discountPercent ? `-${ (discountPercent * 100).toFixed( 0 ) }%` : ''
        };
    }

    /**
     *
     * @param item
     * @param selectedPrice
     * @param dateTimeFormat
     * @param t
     * @param extraParams
     */
    public static createTranslationParamsFromProduct (
        item           : Product,
        selectedPrice  : ProductPriceType,
        dateTimeFormat : string,
        t              : TranslationFunction,
        extraParams   ?: TranslationParams,
    ) : TranslationParams {
        const price : ProductPrice | undefined = SendanorProductUtils.findPrice(item, selectedPrice);
        return this.createTranslationParamsFromPrice(
            price,
            dateTimeFormat,
            t,
            extraParams,
        );
    }

    /**
     *
     * @param item
     * @param priceType
     */
    public static findPrice (
        item      : Product,
        priceType : ProductPriceType,
    ) : ProductPrice | undefined {
        return find(item?.prices ?? [], (price: ProductPrice) : boolean => price?.type === priceType);
    }

    /**
     *
     * @param list
     * @param priceType
     */
    public static findProductsWithPriceType (
        list      : readonly Product[],
        priceType : ProductPriceType,
    ) : readonly Product[] {
        return filter(
            list,
            (item:Product) : boolean => SendanorProductUtils.findPrice(item, priceType) !== undefined
        );
    }

    /**
     *
     * @param product
     * @param productPrice
     */
    public static isSoldOut (
        product      : Product | undefined,
        productPrice : ProductPrice | undefined,
    ): boolean {
        return (
            productPrice?.stockEnabled ? (
                (productPrice?.stockAmount ?? 0) - (productPrice?.stockSold ?? 0) < 1
            ) : false
        ) || (
            product?.stockEnabled ? (
                (product?.stockAmount ?? 0) - (product?.stockSold ?? 0) < 1
            ) : false
        );
    }

}
